import { inject, Injectable, NgZone } from '@angular/core';
import { falsy, leaveNgZone, shareLatest, truthy } from '@nestech/utils';
import { DateTime } from 'luxon';
import {
  BehaviorSubject,
  debounce,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  observeOn,
  of,
  Subject,
  switchMap,
} from 'rxjs';

@Injectable()
export class InactivityService {
  private ngZone = inject(NgZone);

  readonly inactivityNotifySeconds = 5;
  readonly inactivityLogoutSeconds = 60;

  readonly activityEvents: [Document | Window, string][] = [
    [document, 'click'],
    [document, 'wheel'],
    [document, 'scroll'],
    [document, 'mousemove'],
    [document, 'keyup'],
    [window, 'resize'],
    [window, 'scroll'],
    [window, 'mousemove'],
  ];

  readonly refocusEvents: [Document | Window, string][] = [
    [window, 'visibilitychange'],
    [window, 'focus'],
    [window, 'pageshow'],
  ];

  private eventInactivitySeconds$!: Observable<number>;
  private readonly notificationResult$ = new Subject<boolean>();

  private notification$ = new BehaviorSubject<Observable<boolean> | undefined>(undefined);

  constructor() {
    this.ngZone.runOutsideAngular(() => {
      this.eventInactivitySeconds$ = merge(
        ...this.activityEvents.map(aEvents => fromEvent(aEvents[0], aEvents[1])),
        this.notification$.pipe(falsy()),
      ).pipe(
        map(() => DateTime.now()),
        debounce(() =>
          this.notification$.value
            ? this.notification$.pipe(
                truthy(),
                switchMap(n => n),
              )
            : of(true),
        ),
        switchMap(lastActionDateTime =>
          merge(interval(1000), ...this.refocusEvents.map(rEvents => fromEvent(rEvents[0], rEvents[1]))).pipe(
            map(() => Math.round((+DateTime.now() - +lastActionDateTime) / 1000)),
          ),
        ),
        shareLatest(),
      );
    });
  }

  getInactivitySeconds(): Observable<number> {
    return of(null).pipe(
      observeOn(leaveNgZone(this.ngZone)),
      switchMap(() => this.eventInactivitySeconds$),
    );
  }
}
